import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { isMobile } from 'react-device-detect';
import { createPortal } from "react-dom";
import Draggable from "react-draggable";

import Viewport from "../components/Viewport";
import LayerList from "../components/LayerList";
import OtherUser from "../components/OtherUser";
import ScreenshotModal from "../components/ScreenshotModal";
import { ScreenshotGuest } from "../components/ScreenshotGuest";

import { useObjectStore } from "../zustand/objects";
import { useShallow } from "zustand/react/shallow";
import { useAccountStore } from "../zustand/account";

import ArtboardPanel from "../components/ArtboardPanel";
import TopProjectAppBar from "../components/TopProjectAppBar";
import EditorLeftNavbar from "../components/EditorLeftNavbar";
import TextModal from "../components/TextModal";
import CommentSidebar from "../components/CommentSidebar";
import { UploadModal } from "../components/UploadModal";
import { AIModal } from "../components/AIModal";
import EditorRealtimeFooter from "../components/EditorRealtimeFooter";

import { AppShell, Tabs, Text, AppShellAside, AppShellSection, Badge, LoadingOverlay, Group, Button, Dialog, Stack, Box, ActionIcon, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconChevronLeft,
  IconChevronRight,
  IconGripVertical,
  IconMaximize,
  IconX,
} from '@tabler/icons-react';

import { editor } from "../styleFile.js"
import { getEditorChromeTheme, getCollaboratorsChromeTheme, collaboratorsListBoxStyle, sectionLabelStyles } from "../utils/editorChromeTheme";
import {
  CANVAS_GUTTER,
  CANVAS_RADIUS,
  CHROME_ASIDE_WIDTH,
  CHROME_FOOTER_HEIGHT,
  CHROME_HEADER_HEIGHT,
  CHROME_NAVBAR_WIDTH,
  CHROME_TAB_BAR_HEIGHT,
  CHROME_TEXT_SIZE,
  CONTROL_RADIUS,
} from "../utils/uiChromeTokens";

import { checkTutorialSeen, updateTutorialSeen, postRealtimeToken } from "../api";
import {
  debouncedRealtimeEventUpdate,
  cancelEditorRealtimeDebouncers,
  isPresenceUpdateType,
} from "../utils/realtimeDebounce";
import { subscribeThreadCommentsCrossTab } from "../utils/commentCrossTabSync";

function useQuery() {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
}

const DialogueDocs = () => {
  const { helpOpen, setHelpOpen } = useAccountStore()
  const [dialogueStatus, setDialogueStatus] = useState(1)

  const checkTutorialStatus = useCallback(() => {
    checkTutorialSeen().then(res => setHelpOpen(!res.tutorialSeen.editor))
  }, [setHelpOpen])

  useEffect(() => {
    checkTutorialStatus()
  }, [checkTutorialStatus])

  const dialogueTitle = {
    1: '1/6: The canvas',
    2: '2/6: Project and content',
    3: '3/6: State changing',
    4: '4/6: Share and preview',
    5: '5/6: Configure scene',
    6: '6/6: Collaborating'
  }

  const dialogueDescription = {
    1: 'Click and drag to rotate your point of view. Do the same with shift held down to pan. Zoom using your mouse wheel or two fingers on your trackpad.',
    2: 'In the top left you can change page, undo/redo, rename your project and trigger these instructions. In the left toolbar you can add uploaded models, plain text, Figma frames, pre-made assets, templates and comments here. Or generate 3D models with AI.',
    3: 'Use these central buttons to undo/redo, change scene and jump to some default camera positions.',
    4: 'Here are options to preview your concept in first person and share preview links.',
    5: 'Use this sidebar to browse and edit objects, scenes and comments.',
    6: 'Here you can view who is online, bring them to your scene and observe what users can see in preview.'
  }

  const dialoguePosition = {
    1: { top: '75%', left: '1%' },
    2: { top: '6.5%', left: '1%' },
    3: { top: '6.5%', left: '40%' },
    4: { top: '6.5%', left: '71%' },
    5: { top: '30%', left: '54%' },
    6: { top: '75%', left: '54%' }
  }

  const resetStatus = () => {
    setDialogueStatus(1)
  }



  const closeClick = () => {
    setHelpOpen(false)
    setTimeout(resetStatus, 250);
    updateTutorialSeen({
      tutorialPage: "editor",
      tutorialSeen: true
    })
  }

  return (
    <Dialog
      position={dialoguePosition[dialogueStatus]}
      opened={helpOpen} withCloseButton onClose={() => closeClick()} size="lg" radius={4}>
      <Text size="sm" mb="xs" fw={500}>
        {dialogueTitle[dialogueStatus]}
      </Text>

      <Group align="flex-end">
        <Text size="xs" mb="xs" fw={400}>
          {dialogueDescription[dialogueStatus]}
        </Text>
        <Button color="black" disabled={dialogueStatus === 1} onClick={() => setDialogueStatus(dialogueStatus - 1)}>Back</Button>
        <Button color="black" disabled={dialogueStatus === 6} onClick={() => setDialogueStatus(dialogueStatus + 1)}>Next</Button>
      </Group>
    </Dialog>
  )
}


const EditorPage = () => {

  let query = useQuery();

  const queryString = query.get("artboard")

  const { projectKey } = useParams();

  const presence = useObjectStore((s) => s.presence);
  const realtimeClientSocketId = useObjectStore((s) => s.realtimeClientSocketId);
  const viewportThemeOverride = useObjectStore((s) => s.viewportThemeOverride);
  const isDarkLeftNav = viewportThemeOverride === "dark";
  const chromeTheme = getEditorChromeTheme(isDarkLeftNav);
  const backgroundFill = chromeTheme.workspaceBg;

  const otherCollaboratorSocketIds = useMemo(
    () => Object.keys(presence ?? {}).filter((id) => id !== realtimeClientSocketId),
    [presence, realtimeClientSocketId]
  );
  const collaboratorsOnline = otherCollaboratorSocketIds.length;

  const {
    currentObjectArtboard,
    xanoClient,
    screenshotStatus,
    tabValue,
    setTabValue,
    getSpecificUserProject,
    artboards,
    loadObjects,
    updateArtboard,
    undoing,
    unloadObjects,
    lastProjectSaveAt,
    lastProjectRemoteUpdateAt,
  } = useObjectStore(
    useShallow((s) => ({
      currentObjectArtboard: s.currentObjectArtboard,
      xanoClient: s.xanoClient,
      screenshotStatus: s.screenshotStatus,
      tabValue: s.tabValue,
      setTabValue: s.setTabValue,
      getSpecificUserProject: s.getSpecificUserProject,
      artboards: s.artboards,
      loadObjects: s.loadObjects,
      updateArtboard: s.updateArtboard,
      undoing: s.undoing,
      unloadObjects: s.unloadObjects,
      lastProjectSaveAt: s.lastProjectSaveAt,
    }))
  );

  // Editor UI state (modals + footer)
  const [uploadOpen, setUploadOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  // Footer values (realtime connection + last save) come from realtime state + zustand.

  const openUpload = () => setUploadOpen(true);
  const closeUpload = () => setUploadOpen(false);
  const openAi = () => setAiOpen(true);
  const closeAi = () => setAiOpen(false);

  // Xano realtime: subscribe once per projectKey, tear down on change/unmount (see useEffect below).
  const [realtime, setRealtime] = useState(false);

  useEffect(() => {
    if (!projectKey) return undefined;

    // 1. Authenticate the client BEFORE creating the channel
    const token = localStorage.getItem("user");
    if (token) {
      xanoClient.setRealtimeAuthToken(token);
    }

    let projectChannel;
    try {
      // 2. Create and join the channel
      projectChannel = xanoClient.channel(`project/${projectKey}`, { presence: true });

      if (!projectChannel || typeof projectChannel.on !== "function") {
        return undefined;
      }

      // 3. Attach listeners to the channel
      projectChannel.on("connection_status", (action) => {
        if (action.payload.status === "connected") {
          setRealtime(true);
          postRealtimeToken().then((res) => {
            useAccountStore.getState().updateFirstName(res.firstName);
            useObjectStore.getState().setRealtimeToken(res.id);
          });
        } else if (action.payload.status === "disconnected") {
          setRealtime(false);
        }
      });

      projectChannel.on("presence_update", (action) => {
        const st = useObjectStore.getState();
        if (action.payload.action === "join") {
          st.addOrUpdatePresence(action.payload.presence.socketId, "editor", action);
        } else if (action.payload.action === "leave") {
          st.removePresence(action.payload.presence.socketId);
        }
      });

      projectChannel.on("message", (action) => {
        const st = useObjectStore.getState();
        const realtimeToken = st.realtimeToken;
        const p = action.payload;
        // Ensure payload exists and has an updateType
        if (!p || !p.updateType) return;

        // Handle Presence (Xano may use "PresenceUpdate" or "presenceUpdate")
        if (isPresenceUpdateType(p.updateType)) {
          if (p.function === "bringToMe") {
            const ab = p.artboard;
            if (ab != null && ab !== "") {
              st.updateArtboardAndPreviewCamera(String(ab));
            }
            return;
          }

          if (realtimeToken === p.realtime_token && p.field === "cursor") {
            st.recordRealtimeClientSocketFromPresenceAction(action);
            return;
          }

          const field = p.field;
          const chunk = field && p.update?.[field];
          const socketId = action.client?.socketId;
          if (field != null && chunk != null && socketId != null) {
            st.recordRealtimeClientSocketFromPresenceAction(action);
            st.addOrUpdatePresence(socketId, chunk.page, action);
          }
          return;
        }

        // Project payloads: normalize token (API may use number vs string).
        const msgToken = p.realtime_token ?? p.realtimeToken;
        const sameUser =
          realtimeToken != null &&
          msgToken != null &&
          String(realtimeToken) === String(msgToken);

        // Same-user thread list: refresh immediately (realtime echo shares token across tabs).
        if (
          sameUser &&
          (p.updateType === "threadsDelete" ||
            p.updateType === "threads_delete" ||
            p.updateType === "threadsAdd" ||
            p.updateType === "threads_add" ||
            p.updateType === "threadsUpdate" ||
            p.updateType === "threads_update")
        ) {
          st.loadThreads();
          return;
        }

        // Apply project payloads in every tab (including same account / same realtime_token).
        // Omitting this breaks multi-tab editor sync; local sends already updated the sender.
        debouncedRealtimeEventUpdate(action);
      });
    } catch (err) {
      console.log("Error setting up Xano channel:", err);
    }

    return () => {
      cancelEditorRealtimeDebouncers();
      setRealtime(false);
      useObjectStore.getState().setRealtimeClientSocketId(null);
      if (projectChannel && typeof projectChannel.destroy === "function") {
        projectChannel.destroy();
      }
    };
  }, [projectKey, xanoClient]);

  useEffect(() => {
    if (!projectKey) return undefined;
    return subscribeThreadCommentsCrossTab(projectKey, (threadsId) => {
      const st = useObjectStore.getState();
      if (String(st.viewedThread ?? "") !== String(threadsId)) return;
      st.loadComments(threadsId);
    });
  }, [projectKey]);

  useEffect(() => {
    if (!projectKey) return undefined;
    const id = window.setInterval(() => {
      useObjectStore.getState().pruneStalePresence(28000);
    }, 4000);
    return () => window.clearInterval(id);
  }, [projectKey]);

  // Footer "Saved X ago" is now driven by sendUpdateToChannel timestamps
  // (see zustand `sendUpdateToChannel` in src/zustand/objects.js).

  // have commented out unloadProject() and made getspecificUserProject() the async function
  // not 100% if this will cause issues. but seems to have stopped flickering of new proj. load.

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await Promise.resolve(unloadObjects());
      if (cancelled) return;
      await Promise.resolve(getSpecificUserProject(projectKey));
      if (cancelled) return;
      await Promise.resolve(loadObjects());
      if (cancelled) return;
      await Promise.resolve(
        queryString ? updateArtboard(queryString) : updateArtboard("1")
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [getSpecificUserProject, loadObjects, projectKey, queryString, unloadObjects, updateArtboard]);

  const [mobileAsideOpened] = useDisclosure();
  const [desktopAsideOpened, { toggle: toggleDesktopAside }] = useDisclosure(true);

  const iconColour = {
    normal: chromeTheme.icon,
    hover: chromeTheme.iconHover,
    border: chromeTheme.controlBorder,
    hoverBg: chromeTheme.hoverBg,
    disabledBg: chromeTheme.disabledBg,
    disabledIcon: chromeTheme.disabledIcon,
    divider: chromeTheme.border,
  };
  const rightSidebarTheme = {
    shellBg: chromeTheme.asideBg,
    shellBorder: chromeTheme.border,
    shellShadow: "none",
    tabsListBg: chromeTheme.tabsListBg,
    tabsListBorder: chromeTheme.border,
    tabText: chromeTheme.muted,
    tabTextActive: chromeTheme.title,
    tabUnderline: "#FE9A00",
    panelBg: chromeTheme.asideBg,
  };


  const [otherPreview, setOtherPreview] = useState({ open: false, socketId: null });

  const handleWatchOtherUser = (socketId) => {
    setOtherPreview((prev) => {
      if (prev.socketId === socketId) return { open: !prev.open, socketId };
      return { open: true, socketId };
    });
  };

  const otherPreviewUpdate = otherPreview.socketId
    ? presence?.[otherPreview.socketId]?.update
    : null;

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.body.setAttribute("data-editor-theme", isDarkLeftNav ? "dark" : "light");
    return () => {
      document.body.removeAttribute("data-editor-theme");
    };
  }, [isDarkLeftNav]);

  return (
    <>
      <AppShell
        className="mbx-editor-shell"
        data-editor-theme={isDarkLeftNav ? "dark" : "light"}
        header={{ height: CHROME_HEADER_HEIGHT }}
        navbar={{ width: CHROME_NAVBAR_WIDTH }}
        footer={{ height: CHROME_FOOTER_HEIGHT }}
        padding={0}
        // navbar={{ breakpoint: 'sm', collapsed: { mobile: !mobileOpened, desktop: !desktopOpened } }}
        aside={{ width: CHROME_ASIDE_WIDTH, breakpoint: 'md', collapsed: { mobile: !mobileAsideOpened, desktop: !desktopAsideOpened } }}
        styles={{
          root: {
            minHeight: "100dvh",
            backgroundColor: backgroundFill,
          },
          /* Flow layout + absolute canvas never got a real height; fixed frame uses shell vars instead */
          main: {
            padding: 0,
            paddingInlineStart: 0,
            paddingInlineEnd: 0,
            paddingTop: 0,
            paddingBottom: 0,
            minHeight: 0,
            backgroundColor: "transparent",
          },
        }}
      >

        <LoadingOverlay loaderProps={{ children: <h5>Please wait a few seconds, capturing screenshots...</h5>, color: 'black' }} visible={screenshotStatus} zIndex={1200} overlayProps={{ radius: "sm", blur: 2 }} />
        <DialogueDocs />
        <OtherPreviewOverlay
          open={otherPreview.open}
          socketId={otherPreview.socketId}
          update={otherPreviewUpdate}
          isDark={isDarkLeftNav}
          onClose={() => setOtherPreview((p) => ({ ...p, open: false }))}
        />
        <UploadModal open={uploadOpen} close={closeUpload} />
        <AIModal open={aiOpen} close={closeAi} />
        <TextModal />
        <AppShell.Header>
          <TopProjectAppBar mode={"editor"} projectKey={projectKey} />
        </AppShell.Header>
        <AppShell.Main p={0}>
          <Box
            style={{
              position: "fixed",
              zIndex: 1,
              top: `calc(var(--app-shell-header-offset, ${CHROME_HEADER_HEIGHT}px) + ${CANVAS_GUTTER}px)`,
              left: `calc(var(--app-shell-navbar-offset, ${CHROME_NAVBAR_WIDTH}px) + ${CANVAS_GUTTER}px)`,
              right: `calc(var(--app-shell-aside-offset, ${CHROME_ASIDE_WIDTH / 16}rem) + ${CANVAS_GUTTER}px)`,
              bottom: `calc(var(--app-shell-footer-offset, ${CHROME_FOOTER_HEIGHT}px) + ${CANVAS_GUTTER}px)`,
              borderRadius: CANVAS_RADIUS,
              overflow: "hidden",
              backgroundColor: backgroundFill,
            }}
          >
            <Viewport
              undoing={undoing}
              artboards={artboards}
              projectKey={projectKey}
              presence={presence}
            />
          </Box>
        </AppShell.Main>
        <AppShell.Navbar
          p={0}
          style={{
            backgroundColor: chromeTheme.sidebarBg,
            borderRight: `1px solid ${chromeTheme.border}`,
          }}
        >
          <EditorLeftNavbar
            projectKey={projectKey}
            iconColour={iconColour}
            setAiOpen={openAi}
            setUploadOpen={openUpload}
            isDark={isDarkLeftNav}
          />
        </AppShell.Navbar>
        <AppShellAside
          p={0}
          pb={0}
          style={{
            display: "flex",
            flexDirection: "column",
            backgroundColor: rightSidebarTheme.shellBg,
            borderLeft: `1px solid ${rightSidebarTheme.shellBorder}`,
            boxShadow: rightSidebarTheme.shellShadow,
          }}
        >
          {/* Show when artboards isn't null — layout matches Stix sidebar (Figma node 48:1077) */}
          {artboards && currentObjectArtboard && (
            <Tabs
              color="orange"
              style={{
                position: "relative",
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                backgroundColor: rightSidebarTheme.panelBg,
              }}
              value={tabValue} onChange={(val) => setTabValue(val)} pt={0}>
              <Tabs.List
                mb={0}
                style={{
                  flexShrink: 0,
                  height: CHROME_TAB_BAR_HEIGHT,
                  padding: 0,
                  gap: 0,
                  borderBottom: `1px solid ${rightSidebarTheme.tabsListBorder}`,
                  alignItems: "stretch",
                  backgroundColor: rightSidebarTheme.tabsListBg,
                }}
              >
                <Tabs.Tab
                  value="elements"
                  px={12}
                  py={0}
                  style={{ borderRadius: 0 }}
                  sx={{
                    height: CHROME_TAB_BAR_HEIGHT - 1,
                    paddingBottom: 2,
                    borderRadius: `${CONTROL_RADIUS}px ${CONTROL_RADIUS}px 0 0`,
                    borderBottom: "2px solid transparent",
                    marginBottom: -1,
                    color: rightSidebarTheme.tabText,
                    "&[data-active='true']": {
                      borderBottomColor: rightSidebarTheme.tabUnderline,
                      color: rightSidebarTheme.tabTextActive,
                    },
                  }}
                >
                  <Text
                    fw={400}
                    size="sm"
                    style={{
                      fontSize: CHROME_TEXT_SIZE,
                      letterSpacing: "-0.15px",
                      lineHeight: `${CHROME_TEXT_SIZE}px`,
                      color: tabValue === "elements" ? rightSidebarTheme.tabTextActive : rightSidebarTheme.tabText,
                    }}
                  >
                    Elements</Text>
                </Tabs.Tab>
                <Tabs.Tab
                  value="scenes"
                  px={12}
                  py={0}
                  style={{ borderRadius: 0 }}
                  sx={{
                    height: CHROME_TAB_BAR_HEIGHT - 1,
                    paddingBottom: 2,
                    borderRadius: `${CONTROL_RADIUS}px ${CONTROL_RADIUS}px 0 0`,
                    borderBottom: "2px solid transparent",
                    marginBottom: -1,
                    color: rightSidebarTheme.tabText,
                    "&[data-active='true']": {
                      borderBottomColor: rightSidebarTheme.tabUnderline,
                      color: rightSidebarTheme.tabTextActive,
                    },
                  }}
                >
                  <Text
                    fw={400}
                    size="sm"
                    style={{
                      fontSize: CHROME_TEXT_SIZE,
                      letterSpacing: "-0.15px",
                      lineHeight: `${CHROME_TEXT_SIZE}px`,
                      color: tabValue === "scenes" ? rightSidebarTheme.tabTextActive : rightSidebarTheme.tabText,
                    }}
                  >
                    Scenes</Text>
                </Tabs.Tab>
                <Tabs.Tab
                  value="comments"
                  px={12}
                  py={0}
                  style={{ borderRadius: 0 }}
                  sx={{
                    height: CHROME_TAB_BAR_HEIGHT - 1,
                    paddingBottom: 2,
                    borderRadius: `${CONTROL_RADIUS}px ${CONTROL_RADIUS}px 0 0`,
                    borderBottom: "2px solid transparent",
                    marginBottom: -1,
                    color: rightSidebarTheme.tabText,
                    "&[data-active='true']": {
                      borderBottomColor: rightSidebarTheme.tabUnderline,
                      color: rightSidebarTheme.tabTextActive,
                    },
                  }}
                >
                  <Text
                    fw={400}
                    size="sm"
                    style={{
                      fontSize: CHROME_TEXT_SIZE,
                      letterSpacing: "-0.15px",
                      lineHeight: `${CHROME_TEXT_SIZE}px`,
                      color: tabValue === "comments" ? rightSidebarTheme.tabTextActive : rightSidebarTheme.tabText,
                    }}
                  >
                    Comments</Text>
                </Tabs.Tab>
              </Tabs.List>

              {/* MODEL CONTENT */}
              <Tabs.Panel value="elements" h={'100%'} style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <AppShellSection
                  className="mbx-hide-scrollbar"
                  h={'100%'}
                  style={{ ...editor.sidebarSection, flex: 1, minHeight: 0 }}
                  pl={16}
                  pr={16}
                  pt={16}
                >
                  <LayerList mobile={true} />
                </AppShellSection>
              </Tabs.Panel>

              {!desktopAsideOpened && <div style={editor.sidebarAccordion} />}

              {/* SCENE CONTENT */}
              <Tabs.Panel value="scenes" h={'100%'} style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <AppShellSection
                  className="mbx-hide-scrollbar"
                  h={'100%'}
                  style={{ ...editor.sidebarSection, flex: 1, minHeight: 0 }}
                  pl={16}
                  pr={16}
                  pt={16}
                >
                  <ArtboardPanel projectKey={projectKey} mobile={true} />
                </AppShellSection>
              </Tabs.Panel>

              {/* COMMENT CONTENT */}
              <Tabs.Panel value="comments" h={'100%'} style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <AppShellSection
                  className="mbx-hide-scrollbar"
                  h={'100%'}
                  style={{ ...editor.sidebarSection, flex: 1, minHeight: 0 }}
                  pl={16}
                  pr={16}
                  pt={16}
                >
                  {/* <AppShellSection h={'55vh'} style={editor.sidebarSection}> */}
                  {/* <AppShellSection style={editor.sidebarSection}> */}
                  <CommentSidebar />
                </AppShellSection>
              </Tabs.Panel>
            </Tabs>
          )}

          {/* OTHERS – want this to expand contract as it gets taller and overlap the Scene Content section above */}
          <AppShellSection
            pl={16}
            pr={16}
            pt={8}
            pb={8}
            style={{
              flexShrink: 0,
              marginTop: "auto",
              borderTop: `1px solid ${chromeTheme.border}`,
              backgroundColor: chromeTheme.asideBg,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Others
              presence={presence}
              otherSocketIds={otherCollaboratorSocketIds}
              onWatchClick={handleWatchOtherUser}
            />
          </AppShellSection>
        </AppShellAside>

        {artboards && currentObjectArtboard && (
          <Tooltip label={desktopAsideOpened ? "Close sidebar" : "Open sidebar"} position="left" withArrow>
            <ActionIcon
              variant="default"
              color="gray"
              size="md"
              radius={4}
              onClick={toggleDesktopAside}
              aria-label={desktopAsideOpened ? "Close sidebar" : "Open sidebar"}
              style={{
                position: "fixed",
                top: "50%",
                zIndex: 150,
                transform: "translateY(-50%)",
                right: desktopAsideOpened ? CHROME_ASIDE_WIDTH + 8 : 8,
                border: `1px solid ${chromeTheme.border}`,
                backgroundColor: chromeTheme.asideBg,
                color: chromeTheme.title,
                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
              }}
            >
              {desktopAsideOpened ? (
                <IconChevronRight size={14} />
              ) : (
                <IconChevronLeft size={14} />
              )}
            </ActionIcon>
          </Tooltip>
        )}

        <AppShell.Footer withBorder={false} zIndex={2000} style={{ padding: 0 }}>
          <EditorRealtimeFooter
            connected={realtime}
            lastSaveAt={lastProjectSaveAt}
            lastRemoteUpdateAt={lastProjectRemoteUpdateAt}
          />
        </AppShell.Footer>
      </AppShell >
    </>
  )
}

export default EditorPage;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const OtherPreviewOverlay = ({ open, socketId, update, onClose, isDark }) => {
  const [maximised, setMaximised] = useState(false);

  const storageKey = useMemo(() => {
    if (!socketId) return null;
    return `otherUserPreviewPos:${socketId}`;
  }, [socketId]);

  const [dragPos, setDragPos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!storageKey) return;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return setDragPos({ x: 0, y: 0 });
      const parsed = JSON.parse(raw);
      if (typeof parsed?.x === "number" && typeof parsed?.y === "number") {
        setDragPos({ x: parsed.x, y: parsed.y });
      } else {
        setDragPos({ x: 0, y: 0 });
      }
    } catch (_) {
      setDragPos({ x: 0, y: 0 });
    }
  }, [storageKey]);

  useEffect(() => {
    if (!open) setMaximised(false);
  }, [open]);

  const persistPos = (pos) => {
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(pos));
    } catch (_) {
      // ignore
    }
  };

  const canPortal = typeof document !== "undefined" && !!document.body;

  if (!open || !socketId || !update?.preview || !canPortal) return null;

  if (maximised) {
    return createPortal(
      <ScreenshotModal
        closeMaximisedClick={() => setMaximised(false)}
        cameraGuest={update.preview}
        artboard={update.preview.artboard}
        name={update.preview.name}
        id={socketId}
      />,
      document.body
    );
  }

  const baseLeft = 20;
  const overlayTheme = isDark
    ? {
        background: "#2C2C2C",
        border: "#475569",
        icon: "#E0E0E0",
      }
    : {
        background: "#FFFFFF",
        border: "#E5E7EB",
        icon: "#4A5565",
      };

  const baseTop = 80;
  const boxWidth = 250;
  const boxHeight = 132;
  const safeMargin = 10;

  const maxX =
    typeof window !== "undefined" && window.innerWidth
      ? window.innerWidth - baseLeft - boxWidth - safeMargin
      : dragPos.x;
  const maxY =
    typeof window !== "undefined" && window.innerHeight
      ? window.innerHeight - baseTop - boxHeight - safeMargin
      : dragPos.y;

  const clampedPos = {
    x: clamp(dragPos.x, -baseLeft + safeMargin, maxX),
    y: clamp(dragPos.y, -baseTop + safeMargin, maxY),
  };

  if (clampedPos.x !== dragPos.x || clampedPos.y !== dragPos.y) {
    queueMicrotask?.(() => {
      setDragPos(clampedPos);
      persistPos(clampedPos);
    });
  }

  return createPortal(
    <Draggable
      handle=".otherUser__dragHandle"
      bounds="body"
      position={clampedPos}
      onDrag={(_, data) => setDragPos({ x: data.x, y: data.y })}
      onStop={(_, data) => {
        const next = { x: data.x, y: data.y };
        setDragPos(next);
        persistPos(next);
      }}
    >
      <div
        style={{
          position: "fixed",
          left: baseLeft,
          top: baseTop,
          height: boxHeight,
          width: boxWidth,
          borderRadius: 4,
          border: `1px solid ${overlayTheme.border}`,
          overflow: "hidden",
          background: overlayTheme.background,
          zIndex: 2500,
        }}
      >
        <Group bg={"none"} style={{ position: "absolute", right: 5, top: 5, zIndex: 99999 }}>
          <Text>
            <IconGripVertical
              className="otherUser__dragHandle"
              size={20}
              style={{ cursor: "grab", marginRight: 6, color: overlayTheme.icon }}
            />
            <IconMaximize
              size={20}
              onClick={() => setMaximised(true)}
              style={{ marginRight: 5, cursor: "pointer", color: overlayTheme.icon }}
            />
            <IconX size={20} onClick={onClose} style={{ cursor: "pointer", color: overlayTheme.icon }} />
          </Text>
        </Group>

        <ScreenshotGuest cameraGuest={update.preview} artboard={update.preview.artboard} id={socketId} />
      </div>
    </Draggable>,
    document.body
  );
};



const Others = ({ presence, otherSocketIds, onWatchClick }) => {
  const [open] = useState(false)
  const handleBring = () => {
    const o = useObjectStore.getState();
    const { firstName } = useAccountStore.getState();
    o.sendPresenceUpdateToChannel({
      updateType: "presenceUpdate",
      function: "bringToMe",
      projectKey: o.projectKey,
      artboard: String(o.currentObjectArtboard ?? "1"),
      name: firstName,
    });
  };

  const viewportThemeOverride = useObjectStore((s) => s.viewportThemeOverride);
  const isDark = viewportThemeOverride === "dark";
  const onlineCount = otherSocketIds.length;
  const listMaxH = editor.sidebarOthersUsersMaxHeight;
  const collabTheme = getCollaboratorsChromeTheme(isDark);

  return (
    <Stack gap={onlineCount > 0 ? 8 : 4} p={0}>
      <Group justify="space-between" align="center" wrap="nowrap" gap={8}>
        <Text
          fw={600}
          size={11}
          mb={0}
          lh="16px"
          tt="uppercase"
          style={{
            ...sectionLabelStyles(collabTheme),
            color: collabTheme.heading,
          }}
        >
          Collaborators
        </Text>
          <Badge
            size="sm"
            variant="light"
            styles={{
              root: {
                backgroundColor: collabTheme.badgeBg,
                color: collabTheme.badgeText,
                fontWeight: 500,
                fontSize: 10,
                lineHeight: "15px",
                height: 19,
                minHeight: 19,
                paddingLeft: 8,
                paddingRight: 8,
                borderRadius: 4,
                border: "none",
              },
            }}
          >
            {onlineCount}
          </Badge>
      </Group>
      {onlineCount > 0 ? (
        <Stack
          gap={6}
          pt={8}
          pb={8}
          pl={10}
          pr={10}
          style={{
            ...collaboratorsListBoxStyle(collabTheme),
            maxHeight: listMaxH,
            flexShrink: 0,
          }}
        >
          {otherSocketIds.map((mapped, i) => (
            <OtherUser
              open={open}
              key={mapped}
              socketId={mapped}
              update={presence[mapped].update}
              isMobile={isMobile}
              onWatchClick={onWatchClick}
              uiTheme={collabTheme}
            />
          ))}
        </Stack>
      ) : (
        <Text
          size="xs"
          fw={400}
          style={{
            color: collabTheme.emptyText,
            lineHeight: "16px",
          }}
        >
          No one else is here
        </Text>
      )}
      {onlineCount > 0 && (
        <Button
          variant="default"
          size="xs"
          onClick={() => handleBring()}
          styles={{
            root: {
              backgroundColor: collabTheme.buttonBg,
              borderColor: collabTheme.buttonBorder,
              color: collabTheme.buttonText,
            },
          }}
        >
          Bring all to me
        </Button>
      )}
    </Stack>

  )
}


